import {Box, Button, Stack, Tooltip, Typography} from "@mui/material";
import {LoadingButton} from "@/components/input/LoadingButton.tsx";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";
import {FileWarning} from "lucide-react";
import {
  ApprovalStatus,
  ApprovalStepStatus,
  useApproveStepMutation,
  useCreateZyusetuDocumentMutation,
  useRequestApprovalMutation,
  useUndoApprovedStatusMutation,
  useUndoRequestApproveMutation,
  ZyusetuFieldSectionFragmentFragment,
  ZyusetuFragmentFragment
} from "@/graphql/graphql.ts";
import toast from "react-hot-toast";
import {useConfirmDialog} from "@/hooks/use-confirm-dialog.ts";
import {useCallback, useMemo} from "react";
import {useUserData} from "@/hooks/use-user-data-provider.ts";
import {FormProvider, useForm} from "react-hook-form";
import {FormTextField} from "@/components/form/FormTextField.tsx";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {FormRadio} from "@/components/form/FormRadio.tsx";
import {useAtom} from "jotai/index";
import {ZyusetuAtom} from "@/plugin/jotai-wrapper.ts";

interface ZyusetuConfirmSectionProps {
  zyusetu: ZyusetuFragmentFragment
  editType: ZyusetuEditType
  approvalStepExecutionId?: string | null
}

export function ZyusetuConfirmSection(
  {
    zyusetu,
    editType,
    approvalStepExecutionId
  }: ZyusetuConfirmSectionProps
) {
  
  const user = useUserData()
  
  const isAllVerified = useMemo(() => {
    return zyusetu.template.sections.every(
      (section: ZyusetuFieldSectionFragmentFragment) =>
        section.keys.every((field) => field.value?.isVerified),
    );
  }, [zyusetu.template.sections]);
  
  const createZyusetuDocument = useCreateZyusetuDocumentMutation();
  
  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const approveButton = useMemo(() => {
    
    switch (zyusetu.approvalStatus) {
      case ApprovalStatus.Draft:
        if (user.user?.organization?.useApprovalFlow)
          return <RequestApprovalButton zyusetu={zyusetu} editType={editType} isAllVerified={isAllVerified}/>
        return <CompleteZyusetuButton zyusetu={zyusetu} editType={editType} isAllVerified={isAllVerified}/>
      case ApprovalStatus.InReview:
        if (approvalStepExecutionId)
          return <CompleteReviewButton zyusetu={zyusetu} editType={editType}
                                       approvalStepExecutionId={approvalStepExecutionId}/>
        return <UndoRequestApprovalButton zyusetu={zyusetu} editType={editType} isAllVerified={isAllVerified}/>
      
      case ApprovalStatus.Approved:
        return <UndoRequestStatusButton zyusetu={zyusetu} editType={editType} isAllVerified={isAllVerified}/>
      case ApprovalStatus.Rejected:
        return <RequestApprovalButton zyusetu={zyusetu} editType={editType} isAllVerified={isAllVerified}/>
    }
  }, [approvalStepExecutionId, editType, isAllVerified, user.user?.organization?.useApprovalFlow, zyusetu])
  
  return <>
    <Stack sx={{my: 4}} spacing={4} direction={'row'}>
      <Tooltip
        title={
          !isAllVerified
            ? "すべての項目を確認済みにしてください"
            : `クリックで書類が作成されます。${user.user?.organization?.useApprovalFlow ? "承認依頼は送信されません。" : ""}`
        }
      >
        <Box width={'100%'}>
          <LoadingButton
            variant={'outlined'}
            disabled={!isAllVerified}
            onClick={async () => {
              try {
                const result = await createZyusetuDocument.mutateAsync({
                  zyusetuId: zyusetu.id,
                });
                if (result.createZyusetuDocument?.url) {
                  handleDownload(result.createZyusetuDocument.url, result.createZyusetuDocument.fileName);
                }
              } catch (error) {
                console.error("Failed to create document:", error);
              }
            }}
          >
            書類を出力
          </LoadingButton>
        </Box>
      </Tooltip>
      {approveButton}
    </Stack>
  </>
}

const schema = z.object({
  comment: z.string().optional(),
  result: z.nativeEnum(ApprovalStepStatus)
})

function CompleteReviewButton(
  {zyusetu, editType, approvalStepExecutionId}: ZyusetuConfirmSectionProps
) {
  const confirmDialog = useConfirmDialog()
  const form = useForm({
    defaultValues: {
      comment: '',
      result: ApprovalStepStatus.Approved
    },
    resolver: zodResolver(schema),
    mode: "onChange"
  })
  
  const approveStep = useApproveStepMutation()
  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  
  const onSubmit = useCallback(async (
    value: z.infer<typeof schema>
  ) => {
    if (editType === ZyusetuEditType.Review && approvalStepExecutionId) {
      const loading = toast.loading("更新中...");
      await approveStep.mutateAsync({
        stepExecutionId: approvalStepExecutionId,
        result: value.result,
        comment: value.comment,
        zyusetuId: zyusetu.id
      })
        .then(
          (result) => {
            if (result.approveStep) {
              setZyusetu(result.approveStep)
              toast.success("承認結果を送信しました", {id: loading})
            } else {
              toast.error("承認結果の送信に失敗しました", {id: loading})
            }
            confirmDialog.close();
          }
        )
        .catch(
          (error) => {
            console.error("Failed to approve step:", error);
            toast.error("承認に失敗しました", {id: loading})
          }
        )
    }
  }, [approvalStepExecutionId, approveStep, confirmDialog, editType, setZyusetu, zyusetu.id])
  return <>
    <LoadingButton
      variant={'contained'}
      disabled={editType === ZyusetuEditType.ReadOnly}
      onClick={async () => {
        confirmDialog.open({
          fullWidth: true,
          icon: <FileWarning color={"error"}/>,
          title: (
            <Typography
              sx={{color: "primary.contrastText"}}
              fontWeight={"bold"}
            >
              承認結果を選択してください
            </Typography>
          ),
          children: (
            <>
              <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <Stack spacing={1}>
                    <Typography variant={'formLabel'}>結果</Typography>
                    <FormRadio
                      control={form.control}
                      name={'result'}
                      defaultValue={ApprovalStepStatus.Approved}
                      options={
                        [
                          {value: ApprovalStepStatus.Approved, label: "承認"},
                          {value: ApprovalStepStatus.Rejected, label: "却下"},
                        ]
                      }/>
                    <Typography variant={'formLabel'}>コメント</Typography>
                    <FormTextField lines={5} name={'comment'} control={form.control} placeholder={'コメント'}/>
                    <Typography variant={'formLabel'} mt={2}>
                      レビューモードで行った変更は、却下された後に作成者によって修正されるまで出力に反映されません。
                      問題がある場合は、コメントを残して却下してください。
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} mt={4} spacing={2} minWidth={"250px"}>
                    <Button
                      fullWidth
                      variant={'outlined'}
                      onClick={async (_) => confirmDialog.close()}
                    >
                      キャンセル
                    </Button>
                    <Button
                      type={'submit'}
                      fullWidth
                      variant={'contained'}
                    >承認依頼を送信する</Button>
                  </Stack>
                </form>
              </FormProvider>
            </>
          ),
        });
      }}
    >
      承認結果を送信
    </LoadingButton>
  </>
}

function CompleteZyusetuButton(
  {zyusetu, editType, isAllVerified}: ZyusetuConfirmSectionProps & { isAllVerified: boolean }) {
  
  const confirmDialog = useConfirmDialog()
  
  const requestApproval = useRequestApprovalMutation()
  
  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  return <>
    <Tooltip
      title={
        editType === ZyusetuEditType.ReadOnly ? '読み取りモードです' :
          !isAllVerified
            ? "すべての項目を確認済みにしてください"
            : "完了済みにする。"
      }
    >
      <Box width={'100%'}>
        <LoadingButton
          variant={'contained'}
          disabled={!isAllVerified}
          onClick={async () => {
            confirmDialog.open({
              fullWidth: true,
              icon: <FileWarning color={"error"}/>,
              title: (
                <Typography
                  sx={{color: "primary.contrastText"}}
                  fontWeight={"bold"}
                >
                  完了済みに変更送信しますか？
                </Typography>
              ),
              children: (
                <Stack direction={"row"} spacing={2} minWidth={"250px"}>
                  <Button
                    fullWidth
                    variant={'outlined'}
                    onClick={async (_) => confirmDialog.close()}
                  >
                    キャンセル
                  </Button>
                  <Button
                    fullWidth
                    variant={'contained'}
                    onClick={async () => {
                      requestApproval.mutateAsync({
                        zyusetuId: zyusetu.id,
                      }).then(() => {
                        setZyusetu((prev) => {
                          prev.approvalStatus = ApprovalStatus.Approved
                          return prev
                        })
                        toast.success("完了済みにマークしました")
                        confirmDialog.close()
                      }).catch((error) => {
                        console.error("Failed to mark approval:", error);
                        toast.error("完了済みのマークに失敗しました")
                      })
                    }}
                  >完了済みにする</Button>
                </Stack>
              ),
            });
          }}
        >
          完了済みにする
        </LoadingButton>
      </Box>
    </Tooltip></>
}

function UndoRequestStatusButton(
  {zyusetu, isAllVerified}: ZyusetuConfirmSectionProps & { isAllVerified: boolean }
) {
  const confirmDialog = useConfirmDialog()
  
  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  
  const undoApproval = useUndoApprovedStatusMutation()
  
  return (
    <Tooltip
      title={
        !isAllVerified
      }
    >
      <Box width={'100%'}>
        <LoadingButton
          variant={'contained'}
          disabled={!isAllVerified}
          onClick={async () => {
            confirmDialog.open({
              fullWidth: true,
              icon: <FileWarning color={"error"}/>,
              title: (
                <Typography
                  sx={{color: "primary.contrastText"}}
                  fontWeight={"bold"}
                >
                  承認状態を取り消しますか？
                </Typography>
              ),
              children: (
                <Box>
                  <Stack spacing={1}>
                    <Typography variant={'formLabel'} mt={2}>
                      承認状態を取り消すこどで、下書き状態に戻し編集することができます。
                      承認フローが設定されている場合は再度承認をレビュアーに求める必要があります。
                    </Typography>
                  </Stack>
                  <Stack direction={"row"} mt={4} spacing={2} minWidth={"250px"}>
                    <Button
                      fullWidth
                      variant={'outlined'}
                      onClick={async (_) => confirmDialog.close()}
                    >
                      キャンセル
                    </Button>
                    <Button
                      fullWidth
                      variant={'contained'}
                      onClick={async () => {
                        undoApproval.mutateAsync({
                          zyusetuId: zyusetu.id,
                        }).then(() => {
                          toast.error("承認状態を取り消しました")
                          setZyusetu((prev) => {
                            prev.approvalStatus = ApprovalStatus.Draft
                            prev.approvalFlowId = null
                            prev.approvalFlow = null
                            return prev
                          })
                          confirmDialog.close()
                        }).catch((error) => {
                          console.error("Failed to request approval:", error);
                          toast.error("承認状態の取り消しに失敗しました")
                        })
                      }}
                    >取り消す</Button>
                  </Stack>
                </Box>
              ),
            });
          }}
        >
          承認状態を取り消す
        </LoadingButton>
      </Box>
    </Tooltip>
  )
}

function UndoRequestApprovalButton(
  {zyusetu, isAllVerified}: ZyusetuConfirmSectionProps & { isAllVerified: boolean }
) {
  const confirmDialog = useConfirmDialog()
  
  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  
  const undoApproval = useUndoRequestApproveMutation()
  
  return (
    <Tooltip
      title={
        !isAllVerified
      }
    >
      <Box width={'100%'}>
        <LoadingButton
          variant={'contained'}
          disabled={!isAllVerified}
          onClick={async () => {
            confirmDialog.open({
              fullWidth: true,
              icon: <FileWarning color={"error"}/>,
              title: (
                <Typography
                  sx={{color: "primary.contrastText"}}
                  fontWeight={"bold"}
                >
                  承認依頼を取り消しますか？
                </Typography>
              ),
              children: (
                <Stack direction={"row"} spacing={2} minWidth={"250px"}>
                  <Button
                    fullWidth
                    variant={'outlined'}
                    onClick={async (_) => confirmDialog.close()}
                  >
                    キャンセル
                  </Button>
                  <Button
                    fullWidth
                    variant={'contained'}
                    onClick={async () => {
                      undoApproval.mutateAsync({
                        zyusetuId: zyusetu.id,
                      }).then(() => {
                        setZyusetu((prev) => {
                          prev.approvalStatus = ApprovalStatus.Draft
                          prev.approvalFlowId = null
                          prev.approvalFlow = null
                          return prev
                        })
                        toast.success("承認依頼を取り消しました")
                        confirmDialog.close()
                      }).catch((error) => {
                        console.error("Failed to request approval:", error);
                        toast.error("承認依頼の送信に失敗しました")
                      })
                    }}
                  >取り消す</Button>
                </Stack>
              ),
            });
          }}
        >
          承認依頼を取り消す
        </LoadingButton>
      </Box>
    </Tooltip>
  )
}

function RequestApprovalButton(
  {zyusetu, editType, isAllVerified}: ZyusetuConfirmSectionProps & { isAllVerified: boolean }
) {
  const confirmDialog = useConfirmDialog()
  
  const allowSendApprove = useMemo(() => {
    switch (zyusetu.approvalStatus) {
      case ApprovalStatus.InReview:
        return editType === ZyusetuEditType.Review;
      default:
        return true
    }
  }, [editType, zyusetu.approvalStatus])
  
  const approvalStatusWarning = useMemo(() => {
    switch (zyusetu.approvalStatus) {
      case ApprovalStatus.Draft:
        return "承認依頼を送信してください";
      case ApprovalStatus.Approved:
        return "承認済みです";
      case ApprovalStatus.InReview:
        return "承認依頼中です。";
      case ApprovalStatus.Rejected:
        return "却下されました。再送信してください";
    }
  }, [zyusetu.approvalStatus])
  
  const [_, setZyusetu] = useAtom(ZyusetuAtom);
  const requestApproval = useRequestApprovalMutation()
  
  return <>
    <Tooltip
      title={
        !isAllVerified
          ? "すべての項目を確認済みにしてください"
          : !allowSendApprove ? approvalStatusWarning : "承認依頼を送信します。"
      }
    >
      <Box width={'100%'}>
        <LoadingButton
          variant={'contained'}
          disabled={!isAllVerified || !allowSendApprove}
          onClick={async () => {
            confirmDialog.open({
              fullWidth: true,
              icon: <FileWarning color={"error"}/>,
              title: (
                <Typography
                  sx={{color: "primary.contrastText"}}
                  fontWeight={"bold"}
                >
                  承認依頼を送信しますか？
                </Typography>
              ),
              children: (
                <Stack direction={"row"} spacing={2} minWidth={"250px"}>
                  <Button
                    fullWidth
                    variant={'outlined'}
                    onClick={async (_) => confirmDialog.close()}
                  >
                    キャンセル
                  </Button>
                  <Button
                    fullWidth
                    variant={'contained'}
                    onClick={async () => {
                      requestApproval.mutateAsync({
                        zyusetuId: zyusetu.id,
                      }).then((flow) => {
                        if (flow.requestApprove) {
                          setZyusetu((prev) => {
                            prev.approvalFlowId = flow.requestApprove?.id
                            prev.approvalFlow = flow.requestApprove
                            prev.approvalStatus = ApprovalStatus.InReview
                            return prev
                          })
                          toast.success("承認依頼を送信しました")
                        } else {
                          setZyusetu((prev) => {
                            prev.approvalStatus = ApprovalStatus.Approved
                            return prev
                          })
                          toast.error("承認フローが設定されていないため、完了にマークされました。")
                        }
                        confirmDialog.close()
                      }).catch((error) => {
                        console.error("Failed to request approval:", error);
                        toast.error("承認依頼の送信に失敗しました")
                      })
                    }}
                  >承認依頼を送信する</Button>
                </Stack>
              ),
            });
          }}
        >
          承認依頼
        </LoadingButton>
      </Box>
    </Tooltip>
  </>
}